import React, { useState, useEffect } from "react";
import {
    message,
    Modal,
    Drawer,
    Spin,
    Typography,
    Card,
    Row,
    Col,
    Badge,
    Empty,
    Input,
    Tag,
    Divider,
    Button,
} from "antd";
import {
    FileTextOutlined,
    SearchOutlined,
    CheckCircleFilled,
    AppstoreOutlined,
    EyeOutlined,
} from "@ant-design/icons";
import { templateAPIWithErrorHandling } from "../../../api/feeTemplate/feeTemplate";
import { neutral, spacing } from "../../../theme/tokens";

const { Text, Title } = Typography;
const { Search } = Input;

const formatCurrency = (amount) => {
    return new Intl.NumberFormat("vi-VN", {
        style: "currency",
        currency: "VND",
    }).format(amount);
};

// Component Modal chọn Mẫu khoản thu
const FeeTemplateModal = ({ visible, onCancel, onSuccess }) => {
    const [templates, setTemplates] = useState([]);
    const [filteredTemplates, setFilteredTemplates] = useState([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [templateLoading, setTemplateLoading] = useState(false);
    const [selecting, setSelecting] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [detailVisible, setDetailVisible] = useState(false);
    const [detailTemplate, setDetailTemplate] = useState(null);

    // --- 1. Fetch Dữ liệu Mẫu Khoản Thu ---
    const loadTemplates = async () => {
        setTemplateLoading(true);
        try {
            const response = await templateAPIWithErrorHandling.getTemplateAll();
            if (response.data.success) {
                const templatesData = response.data.data.map(item => ({ 
                    ...item, 
                    key: item.id 
                }));
                setTemplates(templatesData);
                setFilteredTemplates(templatesData);
            }
        } catch (error) {
            message.error("Lỗi tải danh sách mẫu: " + error.message);
        } finally {
            setTemplateLoading(false);
        }
    };

    useEffect(() => {
        if (visible) {
            loadTemplates();
            setSelectedTemplateId(null); // Reset lựa chọn khi mở modal
            setSearchText(""); // Reset search
        }
    }, [visible]);

    // --- 2. Xử lý Tìm kiếm ---
    const handleSearch = (value) => {
        setSearchText(value);
        const filtered = templates.filter(template =>
            template.name.toLowerCase().includes(value.toLowerCase()) ||
            (template.description && template.description.toLowerCase().includes(value.toLowerCase())) ||
            template.target_type.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredTemplates(filtered);
    };

    // --- 3. Xử lý Submit (Chọn Mẫu) ---
    const handleSelect = () => {
        if (!selectedTemplateId) {
            message.warning("Vui lòng chọn một mẫu khoản thu.");
            return;
        }

        setSelecting(true);
        const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
        
        if (selectedTemplate) {
            // Chuẩn hóa dữ liệu
            const normalizedData = {
                ...selectedTemplate,
                status: selectedTemplate.status === true, 
            };
            onSuccess(normalizedData);
            onCancel(); // Đóng Modal
        }
        setSelecting(false);
    };

    // --- 4. Xử lý hiển thị chi tiết ---
    const showDetail = (template, e) => {
        e.stopPropagation(); // Ngăn chặn sự kiện chọn card
        setDetailTemplate(template);
        setDetailVisible(true);
    };

    const closeDetail = () => {
        setDetailVisible(false);
        setDetailTemplate(null);
    };

    // --- 5. Render Template Card ---
    const renderTemplateCard = (template) => {
        const isSelected = selectedTemplateId === template.id;
        
        return (
            <Col xs={24} sm={24} md={12} lg={12} xl={8} key={template.id}>
                <Card
                    hoverable
                    onClick={() => setSelectedTemplateId(template.id)}
                    style={{
                        borderColor: isSelected ? '#0070D7' : '#CBD5E1',
                        borderWidth: isSelected ? 2 : 1,
                        backgroundColor: isSelected ? '#F0F6FD' : 'white',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        height: '100%',
                    }}
                    bodyStyle={{ padding: '16px' }}
                >
                    {/* Icon chọn */}
                    {isSelected && (
                        <CheckCircleFilled
                            style={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                fontSize: 24,
                                color: '#0070D7',
                            }}
                        />
                    )}

                    {/* Icon xem chi tiết */}
                    {!isSelected && (
                        <Button
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={(e) => showDetail(template, e)}
                            style={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                            }}
                            title="Xem chi tiết"
                        />
                    )}

                    {/* Header với Icon */}
                    <div style={{ marginBottom: 12 }}>
                        <FileTextOutlined style={{ fontSize: 24, color: '#0070D7', marginRight: 8 }} />
                        <Text strong style={{ fontSize: 16 }}>
                            {template.name}
                        </Text>
                    </div>

                    {/* Description */}
                    <Text type="secondary" style={{ 
                        fontSize: 13, 
                        display: 'block',
                        marginBottom: 12,
                        minHeight: 40,
                        lineHeight: '1.4'
                    }}>
                        {template.description || 'Không có mô tả'}
                    </Text>

                    <Divider style={{ margin: '12px 0' }} />

                    {/* Footer Info */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <Tag color="blue" icon={<AppstoreOutlined />}>
                                {template.target_type}
                            </Tag>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Badge 
                                count={template.items?.length || 0} 
                                showZero 
                                style={{ backgroundColor: '#16A66A' }}
                                title="Số lượng khoản mục"
                            >
                                <Text type="secondary">
                                    Khoản mục
                                </Text>
                            </Badge>
                            {/* <Button
                                type="link"
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={(e) => showDetail(template, e)}
                            >
                                Chi tiết
                            </Button> */}
                        </div>
                    </div>

                    {/* Status Badge (nếu cần) */}
                    {template.status === false && (
                        <div style={{ marginTop: 8 }}>
                            <Tag color="red">Không hoạt động</Tag>
                        </div>
                    )}
                </Card>
            </Col>
        );
    };

    // --- 6. Render Modal Chi tiết ---
    const renderDetailModal = () => {
        if (!detailTemplate) return null;

        return (
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <FileTextOutlined style={{ marginRight: 8, fontSize: 18 }} />
                        <span>Chi tiết: {detailTemplate.name}</span>
                    </div>
                }
                open={detailVisible}
                onCancel={closeDetail}
                footer={[
                    <Button key="close" onClick={closeDetail}>
                        Đóng
                    </Button>,
                    <Button 
                        key="select" 
                        type="primary" 
                        onClick={() => {
                            setSelectedTemplateId(detailTemplate.id);
                            closeDetail();
                        }}
                    >
                        Chọn mẫu này
                    </Button>
                ]}
                width="50%"
            >
                {/* Thông tin tổng quan */}
                <div style={{ marginBottom: 20 }}>
                    <div style={{ marginBottom: 8 }}>
                        <Text strong>Mô tả: </Text>
                        <Text>{detailTemplate.description || 'Không có mô tả'}</Text>
                    </div>
                    {/* <div style={{ marginBottom: 8 }}>
                        <Text strong>Loại đối tượng: </Text>
                        <Tag color="blue">{detailTemplate.target_type}</Tag>
                    </div> */}
                    <div>
                        <Text strong>Trạng thái: </Text>
                        <Tag color={detailTemplate.status ? 'green' : 'red'}>
                            {detailTemplate.status ? 'Hoạt động' : 'Không hoạt động'}
                        </Tag>
                    </div>
                </div>

                <Divider orientation="left">Danh sách khoản mục ({detailTemplate.items?.length || 0})</Divider>

                {/* Bảng chi tiết items */}
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {detailTemplate.items && detailTemplate.items.length > 0 ? (
                        detailTemplate.items.map((item, index) => (
                            <Card
                                key={item.id || index}
                                size="small"
                                style={{ marginBottom: 12 }}
                                bodyStyle={{ padding: '12px 16px' }}
                            >
                                <Row gutter={[16, 8]}>
                                    <Col span={24}>
                                        <Text strong style={{ fontSize: 15, color: '#0070D7' }}>
                                            {index + 1}. {item.item_name || item.name}
                                        </Text>
                                        {item.is_mandatory && (
                                            <Tag color="red" style={{ marginLeft: 8 }}>Bắt buộc</Tag>
                                        )}
                                    </Col>
                                    {/* <Col span={12}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Loại:</Text>
                                        <br />
                                        <Tag color="cyan">{item.type || 'N/A'}</Tag>
                                    </Col>
                                    <Col span={12}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Phương thức tính:</Text>
                                        <br />
                                        <Tag color="purple">{item.calc_method || 'N/A'}</Tag>
                                    </Col> */}
                                    <Col span={12}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Đơn giá: </Text>
                                        {/* <br /> */}
                                        <Text strong style={{ color: '#128555' }}>
                                            {item.unit_price ? formatCurrency(item.unit_price) : '0'}
                                        </Text>
                                    </Col>
                                    <Col span={12}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>Đơn vị: </Text>
                                        {/* <br /> */}
                                        <Text>{item.unit_name || 'N/A'}</Text>
                                    </Col>
                                </Row>
                            </Card>
                        ))
                    ) : (
                        <Empty description="Không có khoản mục nào" />
                    )}
                </div>
            </Modal>
        );
    };

    // --- 7. Custom Footer ---
    const customFooter = (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[2] }}>
            <Button onClick={onCancel}>
                Hủy
            </Button>
            <Button
                type="primary"
                onClick={handleSelect}
                loading={selecting}
                disabled={!selectedTemplateId}
            >
                Chọn Mẫu
            </Button>
        </div>
    );

    return (
        <>
            <Drawer
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <FileTextOutlined style={{ marginRight: 8, fontSize: 20, color: '#0070D7' }} />
                        <span>Chọn Mẫu Khoản Thu</span>
                    </div>
                }
                placement="right"
                width="min(960px, 100vw)"
                open={visible}
                onClose={onCancel}
                destroyOnClose
                styles={{ body: { background: neutral[50], padding: spacing[6] } }}
                footer={customFooter}
            >
            {/* Search Bar */}
            <div style={{ marginBottom: 16 }}>
                <Search
                    placeholder="Tìm kiếm theo tên, mô tả hoặc loại đối tượng..."
                    allowClear
                    enterButton
                    size="large"
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                    onSearch={handleSearch}
                />
            </div>

            {/* Template Cards */}
            <Spin spinning={templateLoading}>
                <div style={{ 
                    maxHeight: '60vh', 
                    overflowY: 'auto',
                    paddingRight: 8 
                }}>
                    {filteredTemplates.length > 0 ? (
                        <Row gutter={[16, 16]}>
                            {filteredTemplates.map(template => renderTemplateCard(template))}
                        </Row>
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                searchText 
                                    ? "Không tìm thấy mẫu khoản thu phù hợp"
                                    : "Chưa có mẫu khoản thu nào"
                            }
                        />
                    )}
                </div>
            </Spin>

            {/* Info Footer */}
            {!templateLoading && filteredTemplates.length > 0 && (
                <div style={{ 
                    marginTop: 16, 
                    paddingTop: 12, 
                    borderTop: '1px solid #F1F5F9',
                    textAlign: 'center'
                }}>
                    <Text type="secondary">
                        Hiển thị {filteredTemplates.length} / {templates.length} mẫu khoản thu
                    </Text>
                </div>
            )}
        </Drawer>

        {/* Modal Chi tiết — cố ý GIỮ là Modal: nó mở đè lên drawer chọn mẫu,
            drawer chồng drawer thì người dùng mất dấu mình đang ở lớp nào. */}
        {renderDetailModal()}
        </>
    );
};

export default FeeTemplateModal;